import { Injectable } from '@angular/core';
import { ProductNavbar } from './product-navbar';
import { MenuItem } from 'primeng/api';
import { Subject, BehaviorSubject, Observable, tap } from 'rxjs';
import { UserSettingsService } from '@user-settings/services/user-settings.service';
import { OidcAuthService } from '@auth/services/oidc-auth.service';
import { take, filter, map, distinctUntilChanged } from 'rxjs';
import { HomepageManagerService } from '@common/homepage/services/homepage-manager.service';
import { MojoUrlProvider } from '@mojo/services/mojo-url-provider';
import { UserSettings } from '@user-settings/models/user-settings';
import { MojoWellKnownNames} from '@common/constants/mojo-well-known-names';
import { UserPermissionsService } from '@permissions/user-permissions';
import { WellKnownRoutePaths } from '@routing/well-known-route-paths';
import { Location } from '@angular/common';
import {MojoRouterEventsService} from "@routing/mojo-router-events.service";

@Injectable({
  providedIn: 'root'
})
export class MojoPmaNavbarService implements ProductNavbar {

  private _systemMenuItems = new BehaviorSubject<MenuItem[]>([]);
  private _userMenuItems = new BehaviorSubject<MenuItem[]>([]);
  private _homepageUrl = new BehaviorSubject<string>(null);
  private currentHomepage: string;

  private _requestPreviewMode = new Subject<string>();

  get requestPreviewMode() {
    return this._requestPreviewMode.asObservable();
  }

  private getHomepageUrl() {
    const hp = this.homepageService.getHomepage().pipe(take(1));
    hp.subscribe((url: string) => {
      this.currentHomepage = url;
      this.setUserMenuItems();
      this._homepageUrl.next(url);
    });
  }

  get systemMenuItems() {
    return this._systemMenuItems.asObservable();
  }

  get userMenuItems() {
    return this._userMenuItems.asObservable();
  }

  userName: Observable<string>;

  get homepageUrl(): Observable<string> {
    return this._homepageUrl.asObservable();
  }

  initialise() {

    const userProfile$ = this.authService.getUserProfile().pipe(
      distinctUntilChanged(),
      filter((profile) => profile != null)
    );
    const userSettings$ = this.userSettingsService.getUserSettings();

    this.userName = userProfile$.pipe(map(profile => profile.databaseDisplayName + '/' + profile.pmaUserDetailsId));

    this.getHomepageUrl();

    userSettings$.subscribe(userSettings => {
      if (userSettings.IsInPreviewMode)
        return;
      this.setSystemMenuItems(userSettings);
    });

    this.setUserMenuItems();
  }

  constructor(
    private readonly homepageService: HomepageManagerService,
    private readonly location: Location,
    private readonly userPermissionsService: UserPermissionsService,
    private readonly mojoUrls: MojoUrlProvider,
    private readonly userSettingsService: UserSettingsService,
    private readonly authService: OidcAuthService,
    routerEvents: MojoRouterEventsService) {

    routerEvents.NavigationEnd$.pipe(
      tap(_ => this.updateHomePageMenuItem())
    ).subscribe();

  }

  private getFullUrlFromPath(): string {
    return window.location.origin + this.location.path();
  }

  private isCurrentUrlSameAsHomePage(): boolean {
    return this.getFullUrlFromPath() === this.currentHomepage;
  }

  private setHomePageItemIcon(homePageItem: MenuItem): void {
    homePageItem.icon = this.isCurrentUrlSameAsHomePage() ? 'fa fa-check fa-m' : null;
  }

  private updateHomePageMenuItem(): void {
    const userMenuItems = this._userMenuItems.value;
    if (!userMenuItems) return;
    const homePageItem = userMenuItems.find(item => item.label === 'Homepage');
    if (!homePageItem) return;
    this.setHomePageItemIcon(homePageItem);
  }

  private setUserMenuItems() {
    const items: MenuItem[] = [];
    const homePageItem = {
      label: 'Homepage',
      command: (() => {
        const url = this.getFullUrlFromPath();
        this.homepageService.setHomepage(url)
          .pipe(take(1))
          .subscribe(() => {
            this.getHomepageUrl();
          });
      })
    };
    this.setHomePageItemIcon(homePageItem);
    items.push(homePageItem);
    this._userMenuItems.next(items);
  }

  private setSystemMenuItems(userSettings: UserSettings) {
    const items: MenuItem[] = [];
    const hasAddOn = (addOnCode: string) => userSettings.EnabledAddOns.includes(addOnCode);
    const hasMci = () => hasAddOn(MojoWellKnownNames.AddOnCodes.WS004_Mci);
    const hasMfs = () => hasAddOn(MojoWellKnownNames.AddOnCodes.WS005_Mfs);
    const hasMft = () => hasAddOn(MojoWellKnownNames.AddOnCodes.WS006_Mft);
    const hasWorks = () => hasAddOn(MojoWellKnownNames.AddOnCodes.WS007_Works);
    const hasFaq = () => userSettings.FaqEnabled;
    const hasAnyProduct = () => hasMci() || hasMfs() || hasMft();
    const isFormsAdmin = () => this.userPermissionsService.isFormsAdministrator();
    const addIf = (c: () => boolean, item: MenuItem) => {
      if (c()) {
        items.push(item);
      }
    }

    items.push({ id: 'systemMenuMyDashboard', label: 'My Dashboard', routerLink: '/main' });
    addIf(hasWorks, { id: 'systemMenuPurchaseToPay', label: 'My Works', url: this.mojoUrls.works(), target: '_self' })
    items.push({ id: 'systemMenuMyActivities', label: 'My Activities', url: this.mojoUrls.myActivities(), target: '_self' });
    items.push({ id: 'systemMenuNewActivity', label: 'New Activity', url: this.mojoUrls.newActivity(), target: '_self' });

    addIf(hasAnyProduct, { separator: true });

    addIf(hasMci, { id: 'systemMenuClientsDashboard', label: 'Client\'s Dashboard', command: () => this._requestPreviewMode.next(MojoWellKnownNames.Products.Mci) });
    addIf(hasMfs, { id: 'systemMenuSuppliersDashboard', label: 'Supplier\'s Dashboard', command: () => this._requestPreviewMode.next(MojoWellKnownNames.Products.MojoForSuppliers) });
    addIf(hasMft, { id: 'systemMenuTenantsDashboard', label: 'Tenant\'s Dashboard', command: () => this._requestPreviewMode.next(MojoWellKnownNames.Products.MojoForTenants) });

    addIf(hasAnyProduct, { separator: true });

    addIf(isFormsAdmin, {
      id: 'systemMenuAdminDashboard',
      label: 'Admin Dashboard',
      routerLink: WellKnownRoutePaths.fromRoot(WellKnownRoutePaths.MojoDashboards.selectProductDashboard)
    });

    addIf(isFormsAdmin, { separator: true });

    addIf(hasFaq, {
      id: 'systemMenuFAQ',
      label: 'FAQ',
      url: '/faq',
      target: '_blank'
    });

    this._systemMenuItems.next(items);
  }

}
